// C4 — the within-session learning curve (PRD 16 §6). *"Readers stop needing
// the glossary."*
//
// PRD 16 calls this one universal: it lands with every buyer segment because it
// is the product proving its own value out of the customer's own data. Which is
// exactly why it is the panel a customer forwards, and the one where a
// fabricated number does the most damage.
//
// Five things here are load-bearing:
//
//  1. **An unmeasured window renders as an absence, never as a curve at zero.**
//     Every number derives from `term_dwell`, which nothing emitted before
//     2026-08-16, so any window reaching into the past has nothing in it. A flat
//     line on the axis would claim "readers needed no glossary time"; the truth
//     is "nothing was measured". The panel prints the backend's own reason and
//     the note that says which of the two this is.
//  2. **Two absences, two different sentences.** "No hover time recorded" is the
//     instrument being silent. "Hover time recorded, nobody re-opened a term" is
//     the instrument working and having nothing to say. The API distinguishes
//     them; the panel renders whichever it returned rather than one generic
//     "no data".
//  3. **Below the floor there is NO share** — the backend's own reason, visible,
//     never "0%". A share of 0 is a real and alarming reading here (nobody ever
//     got quicker), so it cannot double as "we could not tell".
//  4. **Every number states its denominator, and the denominator is term
//     re-openings** — not visits, not readers. One visit that re-opened three
//     terms contributes three.
//  5. **"Within one visit", stated on the panel.** A second look is a second
//     look in the same tab. Nothing here spans two visits and nothing ever will.
//
// The rows lead with the terms readers do NOT get quicker on, because that is
// the only thing on this panel anyone can act on.

import { Card, EmptyState } from "../../ui";
import type { LearningCurveReport, TermLearning } from "./api";
import { formatCount, formatDuration, formatShare, shownOf } from "./format";
import {
  LEARNING_FASTER,
  LEARNING_NO_CHANGE,
  LEARNING_NO_TIMES_WHY,
  LEARNING_NO_VERDICT_WHY,
  LEARNING_ONE_VISIT,
  LEARNING_SHARE_WHY,
  LEARNING_STICKY_LEAD,
  LEARNING_SUBTITLE,
  LEARNING_TITLE,
  LEARNING_TYPICAL_WHY,
  LEARNING_UNIT,
  LEARNING_UNMEASURED_HEADLINE,
  LEARNING_UNMEASURED_NOTE,
} from "./metricLabels";
import "./analytics.css";

const MAX_ROWS = 5;

/** "9.1s → 3.4s". Only ever called with both halves present: a term whose days
 *  all fell under the backend's write-time floor has no times at all, and says
 *  so rather than rendering half an arrow. */
export function formatTransition(first: number, later: number): string {
  return `${formatDuration(first)} → ${formatDuration(later)}`;
}

/** The counted claim, with its denominator attached. Never rendered from a
 *  null share — an absent share prints the backend's reason instead. */
function shareLine(faster: number, total: number): string {
  return `${formatCount(faster)} of ${formatCount(total)} ${LEARNING_UNIT}`;
}

function TermRow({ term }: { term: TermLearning }) {
  // Narrowed on the field itself, not through a boolean: a `?? 0` anywhere near
  // this number would print "0%", which on this panel is a finding rather than
  // an absence.
  const share = term.faster_share;
  const measured = share !== null;
  const times =
    term.first_dwell_ms !== null && term.later_dwell_ms !== null
      ? formatTransition(term.first_dwell_ms, term.later_dwell_ms)
      : null;

  return (
    <li className="maisight-analytics-meter-row">
      <div className="maisight-analytics-meter-head">
        <span className="maisight-analytics-term type-numSm">
          {term.term ?? term.term_id}
        </span>
        <span
          className="maisight-analytics-metric type-numSm"
          title={measured ? LEARNING_SHARE_WHY : undefined}
          data-measured={measured ? "yes" : "no"}
        >
          {measured ? formatShare(share) : "share not measured"}
        </span>
      </div>
      <p className="maisight-analytics-learning-meta type-bodyS">
        {measured ? (
          <>
            <span>{shareLine(term.faster_later, term.re_encounters)}</span>
            <span className="maisight-analytics-learning-verdict">
              {term.verdict === "faster" ? LEARNING_FASTER : LEARNING_NO_CHANGE}
            </span>
          </>
        ) : (
          <span className="maisight-analytics-metric" data-measured="no">
            {`${LEARNING_NO_VERDICT_WHY} ${term.not_measured_reason ?? ""}`}
          </span>
        )}
        {times !== null ? (
          <span title={LEARNING_TYPICAL_WHY}>{`typically ${times}`}</span>
        ) : (
          <span className="maisight-analytics-metric" data-measured="no">
            no times shown
          </span>
        )}
      </p>
    </li>
  );
}

export function LearningCurve({ report }: { report: LearningCurveReport }) {
  const shown = report.terms.slice(0, MAX_ROWS);
  const where = report.doc_title ?? report.doc_id;
  // The headline share, narrowed on the field. `measured` is the API's own
  // flag for "the instrument recorded something"; the share can still be null
  // underneath it when there is too little to rule on.
  const share = report.faster_share;
  const times =
    report.first_dwell_ms !== null && report.later_dwell_ms !== null
      ? formatTransition(report.first_dwell_ms, report.later_dwell_ms)
      : null;

  return (
    <Card
      header={LEARNING_TITLE}
      footer={
        <span className="type-bodyS">
          {report.measured
            ? `${shownOf(shown.length, report.terms_found, "term")} on ${where} · ${report.terms_measured} measured · ${LEARNING_ONE_VISIT}`
            : LEARNING_SUBTITLE}
        </span>
      }
    >
      {!report.measured ? (
        <EmptyState
          headline={LEARNING_UNMEASURED_HEADLINE}
          body={
            <>
              {/* The API's own sentence, so the panel and the backend cannot
               *  drift into disagreeing about what the product can see. */}
              {report.not_measured_reason ?? LEARNING_SUBTITLE}
              <br />
              {LEARNING_UNMEASURED_NOTE}
            </>
          }
        />
      ) : (
        <>
          <div className="maisight-analytics-learning-headline">
            <span
              className="maisight-analytics-metric type-numLg"
              title={share !== null ? LEARNING_SHARE_WHY : undefined}
              data-measured={share !== null ? "yes" : "no"}
            >
              {share !== null ? formatShare(share) : "share not measured"}
            </span>
            <p className="maisight-analytics-learning-meta type-bodyS">
              {share !== null ? (
                <span>
                  {`${shareLine(report.faster_later, report.re_encounters)} were quicker the second time, ${LEARNING_ONE_VISIT}`}
                </span>
              ) : (
                <span className="maisight-analytics-metric" data-measured="no">
                  {`${LEARNING_NO_VERDICT_WHY} ${report.not_measured_reason ?? ""}`}
                </span>
              )}
              {times !== null ? (
                <span title={LEARNING_TYPICAL_WHY}>{`typically ${times}`}</span>
              ) : (
                <span
                  className="maisight-analytics-metric"
                  data-measured="no"
                  title={LEARNING_NO_TIMES_WHY}
                >
                  times not shown
                </span>
              )}
            </p>
          </div>
          <p className="maisight-analytics-learning-note type-bodyS">
            {LEARNING_STICKY_LEAD}
          </p>
          <ul
            className="maisight-analytics-meters"
            aria-label="Terms by how much quicker a second look is"
          >
            {shown.map((term) => (
              <TermRow key={term.term_id} term={term} />
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}
